// filepath: apps/web/src/features/cabinet/chrome/useEnvBadge.ts
//
// FR-UI-CABINET-2 AC-5 — environment badge tone.
//
// The badge displays the current environment (SANDBOX / STAGING /
// PRODUCTION) so users always know whether sandbox-only operations
// like `complete_task ... as_role` (FR-ENV-9) are permitted.
//
// Source: TenantConfig.environment_kind is already on the wire
// (apps/web/src/auth/types.ts:30). The fixture sets it to
// "PRODUCTION"; production backends return the resolved value
// ("SANDBOX" | "STAGING" | "PRODUCTION").
//
// The badge is informational — it does NOT gate any operation. The
// server is the enforcement boundary (FR-ENV-9 / INV-11).

import { useTenantConfig } from "@/bootstrap/tenantConfig";
import { useTenant } from "@/tenant/TenantProvider";
import { APEX_TENANT_SLUG } from "@/apex/apexTenantSlug";

export type EnvTone = "production" | "staging" | "sandbox";

export interface EnvBadgeData {
  /** The label rendered inside the badge. */
  label: string;
  /** The semantic tone — drives background + foreground colour. */
  tone: EnvTone;
}

const KNOWN: Record<string, EnvTone> = {
  PRODUCTION: "production",
  STAGING: "staging",
  SANDBOX: "sandbox",
};

/**
 * Returns the env badge data (label + tone) for the current tenant.
 * Returns `null` while the tenant-config is still loading, so the
 * topbar can render the skeleton variant in that case.
 */
export function useEnvBadge(): EnvBadgeData | null {
  const { tenantSlug } = useTenant();
  // ISS-f4fa1b7b-297: skip the tenant-config fetch on the bare apex
  // (slug === APEX_TENANT_SLUG). The apex landing has no tenant-config
  // (the Go backend's resolver 404s /api/tenant-config on bare apex);
  // passing "" short-circuits the underlying useQuery
  // (enabled: slug !== "").
  const query = useTenantConfig(
    tenantSlug === APEX_TENANT_SLUG ? "" : tenantSlug,
  );
  const raw = query.data?.environment_kind;
  if (typeof raw !== "string" || raw.length === 0) return null;
  const upper = raw.toUpperCase();
  const tone: EnvTone = KNOWN[upper] ?? "production";
  // Display the label in title-case for readability (e.g. "Production"
  // not "PRODUCTION"). The wire value stays uppercase for server-side
  // matching.
  const label = upper.charAt(0) + upper.slice(1).toLowerCase();
  return { label, tone };
}
